import * as React from 'react';
import { createId } from '@paralleldrive/cuid2';
import { toast } from 'react-hot-toast';
import createContext from 'contexts/create-context';
import { Button } from 'components/button/Button';

import { INotificationsState, notificationsReducer } from './reducer';
import { useNotifications } from '../models';
import { useWebsocket } from '../../../contexts/websocket-context';
import { useNavigate } from 'react-router-dom';

export interface INotificationsContext {
  state: INotificationsState;
  refresh: () => void;
}

const [useContext, ReactProvider] = createContext<INotificationsContext>();

export const NotificationsProvider: React.FC<{ children?: React.ReactNode }> = (props) => {
  const { children } = props;
  const navigate = useNavigate();
  const [state, dispatch] = React.useReducer(notificationsReducer, {
    notifications: [],
    hasUnreadNotifications: false,
    lastNotificationTime: 0,
  });
  const { websocket: wsClient } = useWebsocket();

  const { data: notificationsData, mutate: refetch } = useNotifications(100);
  React.useEffect(() => {
    if (notificationsData) {
      dispatch({
        type: 'UPDATE_NOTIFICATIONS',
        notifications: notificationsData,
      });
    }
  }, [notificationsData]);

  React.useEffect(() => {
    if (state.lastNotificationTime > 0 && state.hasUnreadNotifications) {
      const toastId = createId();
      toast(
        <div className="flex gap-4">
          <div>Er zijn nieuwe notificaties</div>
          <div>
            <Button
              size={8}
              color="ghost"
              onTrigger={() => {
                navigate('/internal/notifications');
                toast.dismiss(toastId);
              }}
            >
              Bekijk
            </Button>
          </div>
        </div>,
        {
          id: toastId,
        },
      );
    }
  }, [state.lastNotificationTime]);

  React.useEffect(() => {
    wsClient.send({
      ref: createId(),
      method: 'notifications/subscribe',
      data: {},
    });

    const disposable = wsClient.onMessage((msg) => {
      if (msg.method === 'notifications/new-notification') {
        dispatch({
          type: 'NEW_NOTIFICATION',
          notification: msg.data.notification,
        });
      }
    });
    return () => disposable.dispose();
  }, [true]);

  return (
    <ReactProvider
      value={{
        state,
        refresh: () => {
          // Revalidate the SWR cache (forces a fresh network fetch).
          refetch();
        },
      }}
    >
      {children}
    </ReactProvider>
  );
};

export const useNotificationsCtx = useContext;
